import { findGaps, realEntriesFor } from "../shared/ledger/gaps";
import { biblio, buildAnnualReport } from "../shared/ledger/report";
import { isTestGrade } from "../shared/legal/notice";
import { GRADES, subjectsFor } from "../shared/types";
import type { Entry, Grade } from "../shared/types";
import { createCaptureForm } from "./lib/capture";
import { byId, h, replace, toast } from "./lib/dom";
import {
  activeChild,
  addChild,
  addEntry,
  addMaterial,
  deleteEntry,
  entriesFor,
  eraseState,
  ledgerStats,
  loadState,
  localIsoDate,
  masteryWording,
  removeChild,
  removeMaterial,
  saveState,
  seedExamples,
  setActive,
  toChild,
  toCsv,
  toProgram,
  updateSettings,
} from "./lib/local-ledger";
import type { LedgerChild, LedgerState } from "./lib/local-ledger";
import { dayGroups, entryRow, greeting, joinedSwitch, lastEntryLine, latelyRows, weekStrip } from "./lib/log-view";
import { createCompose, createShell, type TabSpec } from "./lib/shell";

/** The part of the browser's speech recognition this page uses. TypeScript ships no type for it. */
interface Recognizer {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  stop(): void;
}

type RecognizerConstructor = new () => Recognizer;

const PHOTO_MAX_EDGE = 800;
const PHOTO_QUALITY = 0.55;
const REVOKE_AFTER_MS = 10_000;
const DEFAULT_NEW_GRADE: Grade = "Grade 4";
const DEFAULT_CHILD_NAME = "My child";

const MIC_IDLE = "●  Speak";
const MIC_LISTENING = "■  Stop";

const TABS: readonly TabSpec[] = [
  { id: "today", label: "Today", icon: "today" },
  { id: "log", label: "Log", icon: "log" },
  { id: "capture", label: "Log what happened", icon: "plus", center: true },
  { id: "report", label: "Report", icon: "report" },
  { id: "settings", label: "Settings", icon: "settings" },
];

/** Stands in when the browser blocks storage, so the page still works for the length of the visit. */
const NO_STORAGE: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
  getItem: () => null,
  setItem: () => {
    throw new Error("Storage is not available");
  },
  removeItem: () => undefined,
};

function browserStorage(): Pick<Storage, "getItem" | "setItem" | "removeItem"> {
  try {
    return window.localStorage;
  } catch {
    return NO_STORAGE;
  }
}

const storage = browserStorage();
let state: LedgerState = loadState(storage, Date.now());
let pendingPhoto: string | null = null;
let warnedAboutStorage = false;
let justSavedId: string | undefined;

/** Keeps the new state and writes it to this browser. Nothing here touches the network. */
function commit(next: LedgerState): boolean {
  state = next;
  if (saveState(storage, state)) {
    warnedAboutStorage = false;
    return true;
  }
  if (warnedAboutStorage) return false;
  warnedAboutStorage = true;
  toast(
    storage === NO_STORAGE
      ? "This browser is not saving your record. Export it before you close the tab."
      : "Out of room in this browser. Export your record, then erase old photos.",
  );
  return false;
}

function input(id: string): HTMLInputElement {
  return byId<HTMLInputElement>(id);
}

/* Parts that keep their ids */

const parts = { report: () => byId("part-report"), setup: () => byId("part-setup"), export: () => byId("part-export") };
const partNodes: HTMLElement[] = [];

function reclaimParts(): void {
  const box = byId("parts");
  for (const node of partNodes) box.appendChild(node);
}

/* Pages */

function childSwitch(): HTMLElement {
  return joinedSwitch(
    state.children.map((c) => ({ id: c.id, label: c.name })),
    state.active,
    (id) => {
      commit(setActive(state, id));
      shell.render();
    },
    "Child",
  );
}

function topBar(extra?: HTMLElement | null): HTMLElement {
  return h(
    "div",
    { class: "top" },
    h("a", { class: "brand", href: "/" }, "Bearing"),
    h("span", { class: "spacer" }),
    extra === undefined ? childSwitch() : extra,
  );
}

function pageToday(): HTMLElement {
  reclaimParts();
  const now = Date.now();
  const entries = entriesFor(state, state.active);
  const grown = justSavedId;
  justSavedId = undefined;
  const week = weekStrip(entries, now, grown !== undefined);
  const name = activeChild(state).name;
  const page = h("div", { class: "page" }, topBar());
  page.append(
    h(
      "h1",
      { class: "display" },
      greeting(name === DEFAULT_CHILD_NAME ? "Your record" : name, now),
      h("small", {}, lastEntryLine(entries, now)),
    ),
    week.el,
    h(
      "p",
      { class: "hint" },
      `${week.daysLogged} ${week.daysLogged === 1 ? "day" : "days"} logged this week. There is no plan to fall behind on.`,
    ),
    h("h2", { class: "section-label" }, "Lately"),
    ...(entries.length ? latelyRows(entries, now, 4, grown) : [emptyLog()]),
    h("h2", { class: "section-label" }, "On your side"),
    h(
      "p",
      { class: "hint" },
      "This ledger is free and stays on your device. Families who want a person on their side pair with a Learning Guide, who reads the record before every call. That is the paid part.",
    ),
  );
  return page;
}

function emptyLog(): HTMLElement {
  return h(
    "div",
    { class: "empty" },
    h("p", { class: "big" }, "Nothing logged yet."),
    h("p", {}, "Tap the blue button and write one line about what just happened. That is all there is to it."),
    h(
      "button",
      {
        type: "button",
        class: "btn ghost",
        onclick: () => {
          commit(seedExamples(state, Date.now()));
          shell.render();
          toast("Three example days added. Delete them any time.");
        },
      },
      "Show me three example days",
    ),
  );
}

function removeEntry(entry: Entry): void {
  commit(deleteEntry(state, entry.id));
  toast("Deleted.", {
    label: "Undo",
    run: () => {
      commit({ ...state, entries: [...state.entries, entry] });
      justSavedId = entry.id;
      shell.render();
    },
  });
}

function pageLog(): HTMLElement {
  reclaimParts();
  const now = Date.now();
  const entries = entriesFor(state, state.active);
  const grown = justSavedId;
  justSavedId = undefined;
  const page = h("div", { class: "page" }, topBar());
  if (entries.length === 0) {
    page.append(emptyLog());
    return page;
  }
  page.append(
    ...dayGroups(entries, now, {
      rowFor: (entry) => entryRow(entry, { onDelete: removeEntry, grow: entry.id === grown }),
    }),
    h(
      "p",
      { class: "hint", style: "margin-top:16px;text-align:center" },
      "Swipe a line left to delete it. Undo is one tap.",
    ),
  );
  return page;
}

function pageReport(): HTMLElement {
  reclaimParts();
  renderStats();
  const page = h("div", { class: "page" }, topBar(), h("h1", { class: "heading" }, "Annual progress report"));
  page.append(parts.report());
  return page;
}

function pageSettings(): HTMLElement {
  reclaimParts();
  const page = h(
    "div",
    { class: "page" },
    topBar(null),
    h("h1", { class: "heading" }, "Your record"),
    h("p", { class: "lede" }, "Stored in this browser, on this device. Nothing is uploaded."),
  );
  page.append(parts.setup(), parts.export());
  return page;
}

const pages = { today: pageToday, log: pageLog, report: pageReport, settings: pageSettings };

/* Rendering of the parts */

function listRow(body: HTMLElement, removeLabel: string | null, onRemove: () => void): HTMLElement {
  return h(
    "div",
    { class: "row", role: "listitem" },
    body,
    h("span", { class: "spacer" }),
    removeLabel !== null &&
      h("button", { type: "button", class: "del", "aria-label": removeLabel, onclick: onRemove }, "Remove"),
  );
}

function renderMaterials(): void {
  const list = byId("matlist");
  if (state.materials.length === 0) {
    replace(
      list,
      h(
        "p",
        { class: "hint", role: "listitem" },
        "Nothing listed yet. Chapter 12 asks for texts and resources in bibliographic format. Add them as you use them. That is easier than rebuilding the list in June.",
      ),
    );
    return;
  }
  replace(
    list,
    ...state.materials.map((material, index) =>
      listRow(h("span", {}, biblio(material)), `Remove ${material.title || material.author}`, () => {
        commit(removeMaterial(state, index));
        renderMaterials();
      }),
    ),
  );
}

/** Removing a child removes every entry logged for them and cannot be undone, so it keeps a confirmation. */
function confirmRemoveChild(child: LedgerChild): void {
  const question = `Remove ${child.name} and every entry logged for ${child.name}? This cannot be undone. Export first if you want a copy.`;
  if (!window.confirm(question)) return;
  commit(removeChild(state, child.id));
  renderParts();
  shell.render();
}

function renderKidList(): void {
  const canRemove = state.children.length > 1;
  replace(
    byId("kidlist"),
    ...state.children.map((child) =>
      listRow(
        h(
          "span",
          {},
          h("strong", {}, child.name),
          ` · ${child.grade || "Grade not set"} `,
          isTestGrade(child.grade) && h("span", { class: "tag hl" }, "test year"),
        ),
        canRemove ? `Remove ${child.name}` : null,
        () => confirmRemoveChild(child),
      ),
    ),
  );
}

function stat(value: string | number, label: string): HTMLElement {
  return h("div", { class: "stat" }, h("b", {}, String(value)), h("span", {}, label));
}

/** Counts the same entries the report counts, so the two never disagree. Example days are left out. */
function renderStats(): void {
  const stats = ledgerStats(realEntriesFor(state.active, state.entries));
  replace(
    byId("stats"),
    stat(stats.entries, "Entries"),
    stat(stats.days, "Days"),
    stat(stats.hours, "Hours"),
    stat(stats.hoursPerWeek, "Hours a week"),
    stat(stats.subjects, "Subjects"),
  );
}

function renderSettings(): void {
  byId<HTMLTextAreaElement>("mastery").value = state.settings.mastery;
  input("rfrom").value = state.settings.from;
  input("rto").value = state.settings.to;
  input("pname").value = state.settings.parentName;
}

function renderParts(): void {
  renderMaterials();
  renderKidList();
  renderStats();
  renderSettings();
}

/* Report */

function renderGapNote(gaps: readonly string[], hasExamples: boolean): void {
  const note = byId("gapnote");
  note.hidden = gaps.length === 0 && !hasExamples;
  replace(
    note,
    gaps.length > 0 && h("strong", {}, "Before you send it:"),
    gaps.length > 0 && ` ${gaps.join("; ")}.`,
    hasExamples && `${gaps.length > 0 ? " " : ""}The example days are left out of the report.`,
  );
}

function assembleReport(): string {
  const child = toChild(activeChild(state));
  const program = toProgram(state);
  const report = buildAnnualReport({ child, entries: state.entries, program, parentName: state.settings.parentName });
  byId("report").textContent = report;

  const gaps = findGaps({ child, entries: state.entries, program, now: Date.now() });
  const hasExamples = state.entries.some((e) => e.childId === child.id && e.example === true);
  renderGapNote(gaps, hasExamples);
  return report;
}

/* Capture */

const form = createCaptureForm({ onSave: saveEntry, onCancel: () => compose.close() });
const preview = h("img", { class: "thumb", alt: "Attached photo", hidden: true });
const photoInput = h("input", { type: "file", id: "photo", accept: "image/*", capture: "environment", hidden: true });
const mic = h(
  "button",
  { type: "button", class: "btn ghost small", id: "mic", "aria-pressed": "false", hidden: true },
  MIC_IDLE,
);
const micNote = h(
  "p",
  { class: "hint", id: "mic-note", hidden: true },
  "Speak uses your browser’s dictation. Some browsers send the audio to their maker to turn it into text. Typing sends nothing anywhere.",
);

function openCapture(): void {
  if (compose.isOpen) {
    compose.close();
    return;
  }
  const child = activeChild(state);
  form.setQuestion(
    child.name && child.name !== DEFAULT_CHILD_NAME ? `What did ${child.name} do today?` : "What happened today?",
  );
  form.setSubjects(subjectsFor(child.grade));
  compose.open();
  setTimeout(() => form.focus(), 180);
}

function resetCapture(): void {
  pendingPhoto = null;
  form.reset();
  photoInput.value = "";
  preview.hidden = true;
  preview.removeAttribute("src");
}

function saveEntry(): void {
  const draft = form.read();
  const next = addEntry(
    state,
    {
      text: draft.text,
      subjects: draft.subjects,
      minutes: draft.minutes,
      photo: pendingPhoto,
      stuck: draft.stuck,
      askGuide: draft.askGuide,
    },
    Date.now(),
  );
  if (next === state) {
    toast("Add a line or a photo first.");
    form.focus();
    return;
  }
  const saved = commit(next);
  const entry = next.entries[next.entries.length - 1];
  resetCapture();
  compose.close();
  try {
    navigator.vibrate?.(8);
  } catch {
    /* no haptics here */
  }
  justSavedId = entry?.id;
  renderStats();
  shell.render();
  if (saved) toast("Logged.");
}

/** Downscales to 800px on the long edge and re-encodes as JPEG, so a year of photos fits in this browser. */
function shrink(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        resolve(null);
        return;
      }
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const context = canvas.getContext("2d");
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", PHOTO_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

async function attachPhoto(): Promise<void> {
  const file = photoInput.files?.[0];
  if (!file) return;
  const dataUrl = await shrink(file);
  if (dataUrl === null) {
    toast("That image could not be read.");
    return;
  }
  pendingPhoto = dataUrl;
  preview.src = dataUrl;
  preview.hidden = false;
}

function recognizerConstructor(): RecognizerConstructor | undefined {
  const speech: Window & {
    SpeechRecognition?: RecognizerConstructor;
    webkitSpeechRecognition?: RecognizerConstructor;
  } = window;
  return speech.SpeechRecognition ?? speech.webkitSpeechRecognition;
}

function setUpVoice(): void {
  const Recognition = recognizerConstructor();
  if (!Recognition) return;

  const note = form.note;
  const recognizer = new Recognition();
  recognizer.continuous = true;
  recognizer.interimResults = true;
  recognizer.lang = "en-US";
  let listening = false;
  let before = "";

  const showListening = (on: boolean): void => {
    listening = on;
    mic.textContent = on ? MIC_LISTENING : MIC_IDLE;
    mic.setAttribute("aria-pressed", String(on));
  };

  recognizer.onresult = (event) => {
    let heard = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) heard += event.results[i]?.[0]?.transcript ?? "";
    note.value = `${before} ${heard}`.trim();
  };
  recognizer.onend = () => showListening(false);
  recognizer.onerror = () => toast("Microphone not available.");

  mic.hidden = false;
  micNote.hidden = false;
  mic.addEventListener("click", () => {
    if (listening) {
      recognizer.stop();
      return;
    }
    before = note.value;
    try {
      recognizer.start();
      showListening(true);
    } catch {
      toast("Microphone not available.");
    }
  });
}

function setUpCapture(): void {
  const photoButton = h(
    "button",
    { type: "button", class: "btn ghost small", id: "photoBtn", onclick: () => photoInput.click() },
    "+  Photo",
  );
  photoInput.addEventListener("change", () => void attachPhoto());
  form.slot.append(
    h("span", { class: "label" }, "Photo", h("small", {}, "Optional")),
    h("div", { class: "actions" }, photoButton, mic),
    photoInput,
    preview,
    micNote,
  );
  setUpVoice();
}

/* Program */

function useThreeStageWording(): void {
  const mastery = byId<HTMLTextAreaElement>("mastery");
  if (mastery.value.trim() !== "" && !window.confirm("Replace what you wrote with the three-stage wording?")) return;
  mastery.value = masteryWording();
  commit(updateSettings(state, { mastery: mastery.value }));
}

function addMaterialFromForm(): void {
  const next = addMaterial(state, {
    title: input("mtitle").value,
    author: input("mauthor").value,
    publisher: input("mpub").value,
    year: input("myear").value,
  });
  if (next === state) {
    toast("A title or an author, at least.");
    return;
  }
  commit(next);
  for (const id of ["mtitle", "mauthor", "mpub", "myear"]) input(id).value = "";
  renderMaterials();
}

function addChildFromForm(): void {
  const gradeSelect = byId<HTMLSelectElement>("kgrade");
  const grade = GRADES.find((g) => g === gradeSelect.value) ?? DEFAULT_NEW_GRADE;
  const next = addChild(state, input("knew").value, grade, Date.now());
  if (next === state) {
    toast("Give the child a name.");
    return;
  }
  commit(next);
  input("knew").value = "";
  renderParts();
  shell.render();
}

function setUpProgram(): void {
  const gradeSelect = byId<HTMLSelectElement>("kgrade");
  for (const grade of GRADES) gradeSelect.appendChild(h("option", {}, grade));
  gradeSelect.value = DEFAULT_NEW_GRADE;

  const mastery = byId<HTMLTextAreaElement>("mastery");
  mastery.addEventListener("input", () => commit(updateSettings(state, { mastery: mastery.value })));
  input("rfrom").addEventListener("input", () => commit(updateSettings(state, { from: input("rfrom").value })));
  input("rto").addEventListener("input", () => commit(updateSettings(state, { to: input("rto").value })));
  input("pname").addEventListener("input", () => commit(updateSettings(state, { parentName: input("pname").value })));

  byId("mastery-fill").addEventListener("click", useThreeStageWording);
  byId("addmat").addEventListener("click", addMaterialFromForm);
  byId("addkid").addEventListener("click", addChildFromForm);
}

/* Export */

/** A standard browser download. The file is made on this device and goes nowhere else. */
function download(filename: string, data: string, type: string): void {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const link = h("a", { href: url, download: filename, hidden: true });
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
  toast("Saved to your downloads.");
}

function childSlug(): string {
  const slug = activeChild(state)
    .name.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "child";
}

/** Erasing everything cannot be undone, so it keeps a confirmation. */
function eraseEverything(): void {
  const question =
    "Erase every entry, material and child on this device? This cannot be undone. Export first if you want a copy.";
  if (!window.confirm(question)) return;
  eraseState(storage);
  state = loadState(storage, Date.now());
  resetCapture();
  renderParts();
  shell.render();
  toast("Erased.");
}

function setUpExport(): void {
  const stamp = (): string => localIsoDate(Date.now());
  byId("expjson").addEventListener("click", () =>
    download(`learning-ledger-${stamp()}.json`, JSON.stringify(state, null, 2), "application/json"),
  );
  byId("expcsv").addEventListener("click", () =>
    download(`learning-log-${childSlug()}-${stamp()}.csv`, toCsv(state), "text/csv;charset=utf-8"),
  );
  byId("exptxt").addEventListener("click", () =>
    download(`progress-report-${childSlug()}-${stamp()}.txt`, assembleReport(), "text/plain;charset=utf-8"),
  );
  byId("wipe").addEventListener("click", eraseEverything);
}

partNodes.push(parts.report(), parts.setup(), parts.export());
setUpCapture();
setUpProgram();
setUpExport();
byId("build").addEventListener("click", () => assembleReport());
byId("printrep").addEventListener("click", () => {
  assembleReport();
  window.print();
});
renderParts();
const shell = createShell(byId("home"), TABS, pages, { onCenter: openCapture });
const compose = createCompose(byId("app"), shell.button("capture"), form.el, [form.bar, form.question], {
  pushBack: shell.content,
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && compose.isOpen) compose.close();
});
